import React from 'react'
import PropTypes from 'prop-types'
import {
    Box,
    Divider,
    Heading,
    useBreakpointValue,
    useStyleConfig,
    Skeleton
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {FormattedMessage} from 'react-intl'
import {SearchSugggestionTile} from './search-suggestions'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import {SwiperSlide, Swiper} from 'swiper/react'
import {Navigation} from 'swiper/modules'
import 'swiper/css'
import 'swiper/css/navigation'

export const NoSearchSuggestions = ({isLoading, products, closeAndNavigate, recentSearches}) => {
    const isDesktop = useBreakpointValue({base: false, lg: true})
    const {
        app: {commerceAPI}
    } = getConfig()
    const {bloomreach} = commerceAPI
    const {config} = bloomreach || {}
    var enableBloomreachSuggestions = false
    try {
        if (config) {
            enableBloomreachSuggestions = JSON.parse(config).enableBloomreachSuggestions
        }
    } catch (e) {
        console.error(e)
    }
    const suggestionsStyle = useStyleConfig('SuggestionsStyle')
    return (
        <Box
            className="product-suggestions-wrapper"
            display={!isLoading && !products?.length ? 'none' : 'block'}
            {...suggestionsStyle.productSuggestions.main}
            width={{lg: recentSearches?.length ? '100%' : 'fit-content'}}
        >
            {isLoading ? (
                <Skeleton
                    {...suggestionsStyle.productSuggestions.skeleton}
                    marginLeft={{
                        lg: '40px'
                    }}
                />
            ) : (
                <Heading
                    {...suggestionsStyle.productSuggestions.heading}
                    paddingLeft={{
                        lg: recentSearches?.length ? '0px' : '40px'
                    }}
                >
                    {enableBloomreachSuggestions ? (
                        <FormattedMessage defaultMessage="Trending" id="no.result.found" />
                    ) : (
                        <FormattedMessage
                            defaultMessage="Product Suggestions"
                            id="search.product.suggestions"
                        />
                    )}
                </Heading>
            )}
            {isDesktop ? (
                <Box
                    sx={{
                        position: 'relative',
                        maxWidth: '-webkit-fill-available',
                        width: '100%',
                        '.image-wrapper, .chakra-heading': {
                            marginBottom: '10px'
                        },
                        '.swiper': {
                            padding: '0 20px 0 0 !important',
                            margin: '0 !important',
                            overflowX: 'auto',
                            WebkitOverflowScrolling: 'touch',
                            position: 'static',
                            /* Hide scrollbar */
                            '&::-webkit-scrollbar': {
                                display: 'none'
                            },
                            scrollbarWidth: 'none', // for Firefox
                            msOverflowStyle: 'none' // for IE and Edge
                        },
                        '.swiper-wrapper': {
                            paddingLeft: {
                                base: recentSearches?.length ? '0px !important' : '20px',
                                xs: recentSearches?.length ? '0px !important' : '30px',
                                lg: recentSearches?.length ? '0px !important' : '41px'
                            }
                        },
                        '.swiper-slide': {
                            width: '152px !important'
                        },
                        '.swiper-button-next': {
                            right: recentSearches?.length ? '0px' : '118px',
                            top: '-63px'
                        },

                        '.swiper-button-prev': {
                            right: recentSearches?.length ? '50px' : '165px',
                            top: '-63px',
                            left: 'initial'
                        }
                    }}
                    className="desktop-no-result-recommendations"
                >
                    <Box
                        className="swiper-parent"
                        {...suggestionsStyle.productSuggestions.desktopSwiperParent}
                        width={recentSearches?.length ? '100%' : '100vw'}
                    >
                        {/* Swiper Carousel */}
                        <Swiper
                            navigation={true}
                            slidesPerView="auto"
                            spaceBetween="20px"
                            modules={[Navigation]}
                        >
                            {/* Slides */}
                            {isLoading ? (
                                <SwiperSlide style={{flexShrink: 0, width: 'auto'}}>
                                    <Box display="inline-flex">
                                        <Skeleton width="120px" height="150px" marginRight="1rem" />
                                        <Skeleton width="120px" height="150px" />
                                    </Box>
                                </SwiperSlide>
                            ) : (
                                products?.map((product, idx) => (
                                    <SwiperSlide key={idx}>
                                        <Box>
                                            <SearchSugggestionTile
                                                index={idx}
                                                closeAndNavigate={closeAndNavigate}
                                                product={product}
                                            />
                                        </Box>
                                    </SwiperSlide>
                                ))
                            )}
                        </Swiper>
                    </Box>
                </Box>
            ) : (
                // Mobile layout: horizontal scroll
                <Box
                    {...suggestionsStyle.productSuggestions.mobileLayout}
                    width="calc(100vw - 40px)"
                >
                    {isLoading ? (
                        <Box display="inline-flex" margin="1rem 0 0 0">
                            <Skeleton width="120px" height="150px" marginRight="1rem" />
                            <Skeleton width="120px" height="150px" />
                        </Box>
                    ) : (
                        products?.map((product, idx) => (
                            <Box key={idx} {...suggestionsStyle.productSuggestions.suggestionBox}>
                                <SearchSugggestionTile
                                    index={idx}
                                    closeAndNavigate={closeAndNavigate}
                                    product={product}
                                />
                            </Box>
                        ))
                    )}
                </Box>
            )}
            {(isLoading || (!isLoading && products?.length)) && !enableBloomreachSuggestions && (
                <Divider
                    {...suggestionsStyle.divider}
                    margin={{lg: '1rem !important', base: '20px 0 !important'}}
                />
            )}
        </Box>
    )
}

NoSearchSuggestions.propTypes = {
    isLoading: PropTypes.bool,
    recentSearches: PropTypes.any,
    products: PropTypes.arrayOf(PropTypes.object),
    closeAndNavigate: PropTypes.func
}
